// The OEM discount rule table, as anixify.discount.rule actually holds it.
//
// ⚠️ FOUR COLUMNS FROM THE MOCK VERSION ARE GONE, each for want of a source,
// and none may come back on the strength of "the table looks sparse". This is
// the same treatment /quotes got at U1 and /catalog at slice 1c.
//
//   Category    NOT A MATCH KEY. Owner's decision: the keys are
//               (partner_id, brand_id) and nothing else, so one rule covers a
//               whole brand for a customer. A Category column would state that
//               rules are per-category, which would be a lie about how the
//               rates are negotiated.
//   Deal Type   ⚠️ THE WORST OF THE FOUR. It lives on the LINE, not the rule --
//               owner's decision -- because the rule table holds STANDING
//               REGISTERED RATES ONLY. A Deal Type column here would imply the
//               table can hold a competitive bid, which the owner said in as
//               many words it must never.
//   Project ID  nothing in this system identifies a project on a quote line,
//               and the promise that project rules beat client rules was
//               corrected out of Pricing.tsx at U2 because nothing implemented
//               it.
//   Valid To    anixify.discount.rule HAS NO SUCH COLUMN. A rate applies until
//               it is ARCHIVED, not until a date passes -- see Agreed On below.
//
// ⚠️ "Agreed On" IS NOT "Valid From", AND THE LABEL IS LOAD-BEARING.
// effective_from is AUDIT INFORMATION. It records when the rate was agreed and
// it does NOT gate matching. Labelling it "Valid From" would invite exactly
// the reading the owner rejected: that a rate switches on at a date and,
// therefore, off at another. A backend test asserts nothing in the addon
// filters on that column.

import type { OemDiscountRule } from "@/types";
import { formatDate, formatPercent } from "@/lib/format";
import { colors, semanticColors, radius, spacing, heights, typography, shadows } from "@/styles/tokens";

type ColDef = { header: string; width: number; align: "left" | "right" };

const COLS: ColDef[] = [
  { header: "Customer",     width: 220, align: "left"  },
  { header: "OEM",          width: 140, align: "left"  },
  { header: "OEM Disc %",   width: 120, align: "right" },
  { header: "Agreed On",    width: 130, align: "left"  },
  { header: "Notes",        width: 220, align: "left"  },
  // ⚠️ WHO -- RECORDED, NEVER INFERRED. These are the actor the backend
  // stamped from the session, NOT Odoo's create_uid / write_uid: every write
  // on this route goes through as superuser, so Odoo's own columns name
  // OdooBot on all six rules that existed when this landed.
  //
  // ⚠️ AN EMPTY ACTOR RENDERS "Unknown", NOT A DASH. A dash is this table's
  // word for "nothing was recorded and that is fine" -- it is what Agreed On
  // and Notes use. An absent author is not fine, it is a gap, and the column
  // must say so in a word a reader cannot mistake for a blank.
  { header: "Created By",     width: 150, align: "left"  },
  { header: "Last Edited By", width: 150, align: "left"  },
  { header: "Status",       width: 110, align: "left"  },
];

export type RuleStatus = "Active" | "Archived";

/** ⚠️ THE ONLY THING THAT DECIDES WHETHER A RULE APPLIES.
 *
 *  No date is consulted, deliberately. A rate applies until somebody archives
 *  it; superseding is "add the newer rule, archive the old one". If this
 *  function ever grows a date comparison, a rate would silently stop applying
 *  at midnight and lines that priced yesterday would refuse today -- pointing
 *  at a rule visibly sitting in the table looking perfectly valid. */
export function statusOf(rule: OemDiscountRule): RuleStatus {
  return rule.active ? "Active" : "Archived";
}

function StatusPill({ status }: { status: RuleStatus }) {
  const active = status === "Active";
  return (
    <span
      data-testid={`rule-status-${status.toLowerCase()}`}
      style={{
        display: "inline-flex", alignItems: "center",
        height: heights.pill, padding: `0 ${spacing[8]}px`, borderRadius: radius.base,
        fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
        color: active ? colors.green600 : semanticColors.textMuted,
        backgroundColor: active ? colors.green50 : semanticColors.hoverBg,
        border: `1px solid ${active ? colors.green100 : semanticColors.border}`,
      }}
    >
      {status}
    </span>
  );
}

function Dash() {
  return <span style={{ color: semanticColors.textPlaceholder }}>—</span>;
}

/** ⚠️ A WORD, NOT A DASH, AND NOT A BLANK. "Unknown" is a claim: nobody can
 *  say who did this. A blank cell reads as "nothing to report here", and a
 *  dash is what this table already uses for facts that are legitimately
 *  absent (no agreed date, no notes). An unrecorded author is neither -- it
 *  is the gap that made the six live rules untraceable, and the column exists
 *  to show it rather than hide it. Italic so it cannot be misread as a name.
 *
 *  It is NOT an error state: a rule created directly in Odoo has no API actor
 *  and reads Unknown correctly and permanently. */
function UnknownActor() {
  return (
    <span style={{ color: semanticColors.textPlaceholder, fontStyle: "italic" }}>
      Unknown
    </span>
  );
}

export function OemDiscountsTable({
  rows, onRowClick, selectedId,
}: {
  rows: OemDiscountRule[];
  onRowClick: (r: OemDiscountRule) => void;
  selectedId?: string;
}) {
  const totalWidth = COLS.reduce((sum, c) => sum + c.width, 0);

  return (
    <div
      data-testid="oem-discounts-table"
      style={{
        flex: 1, minHeight: 0, overflow: "auto",
        border: `1px solid ${semanticColors.border}`,
        borderRadius: radius.base,
        backgroundColor: semanticColors.surface,
        boxShadow: shadows.sm,
      }}
    >
      <div style={{ minWidth: totalWidth }}>
        <div
          data-testid="oem-discounts-head"
          style={{
            display: "flex", height: heights.control, position: "sticky", top: 0, zIndex: 1,
            backgroundColor: semanticColors.panelBg,
            borderBottom: `1px solid ${semanticColors.border}`,
          }}
        >
          {COLS.map((c) => (
            <div key={c.header} style={{
              width: c.width, minWidth: c.width, padding: `0 ${spacing[12]}px`,
              display: "flex", alignItems: "center",
              justifyContent: c.align === "right" ? "flex-end" : "flex-start",
              fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium,
              color: semanticColors.textSecondary,
              textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
              whiteSpace: "nowrap",
            }}>{c.header}</div>
          ))}
        </div>

        <div>
          {rows.map((r) => {
            const status = statusOf(r);
            const archived = status === "Archived";
            const textColor = archived ? semanticColors.textPlaceholder : semanticColors.textPrimary;
            const isSelected = r.id === selectedId;
            return (
              <div
                key={r.id}
                data-testid={`discount-row-${r.id}`}
                data-status={status}
                className="material-row"
                onClick={() => onRowClick(r)}
                style={{
                  display: "flex", height: heights.row,
                  borderBottom: `1px solid ${semanticColors.border}`,
                  backgroundColor: isSelected ? semanticColors.hoverBg : semanticColors.surface,
                  cursor: "pointer",
                }}
              >
                <div style={{ width: COLS[0].width, minWidth: COLS[0].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                              color: textColor, fontWeight: typography.fontWeight.medium,
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {r.partnerName || <Dash />}
                </div>
                <div style={{ width: COLS[1].width, minWidth: COLS[1].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm, color: textColor }}>
                  {r.brandName || <Dash />}
                </div>
                <div style={{ width: COLS[2].width, minWidth: COLS[2].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", justifyContent: "flex-end",
                              fontSize: typography.fontSize.sm, color: textColor,
                              fontWeight: typography.fontWeight.medium, fontVariantNumeric: "tabular-nums" }}>
                  {formatPercent(r.discountPct)}
                </div>
                <div style={{ width: COLS[3].width, minWidth: COLS[3].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                              color: archived ? semanticColors.textPlaceholder : semanticColors.textMuted,
                              fontVariantNumeric: "tabular-nums" }}>
                  {/* ⚠️ A DASH IS CORRECT AND MEANS "nobody recorded when this
                      was agreed". It does NOT mean the rule is pending or
                      invalid -- a rule with no agreed date applies exactly like
                      one with a date, because the date gates nothing. */}
                  {r.effectiveFrom ? formatDate(r.effectiveFrom) : <Dash />}
                </div>
                <div style={{ width: COLS[4].width, minWidth: COLS[4].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                              color: semanticColors.textMuted,
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {r.notes || <Dash />}
                </div>
                <div data-testid={`discount-created-by-${r.id}`}
                     style={{ width: COLS[5].width, minWidth: COLS[5].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                              color: archived ? semanticColors.textPlaceholder : semanticColors.textMuted,
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {r.createdBy || <UnknownActor />}
                </div>
                <div data-testid={`discount-last-edited-by-${r.id}`}
                     style={{ width: COLS[6].width, minWidth: COLS[6].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                              color: archived ? semanticColors.textPlaceholder : semanticColors.textMuted,
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {r.lastEditedBy || <UnknownActor />}
                </div>
                <div style={{ width: COLS[7].width, minWidth: COLS[7].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center" }}>
                  <StatusPill status={status} />
                </div>
              </div>
            );
          })}

          {rows.length === 0 && (
            <div
              data-testid="oem-discounts-empty-filter"
              style={{
                padding: spacing[24], textAlign: "center",
                fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
              }}
            >
              No discount rules match the current filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
